import { AlertTriangleIcon, Box, HStack, Icon, Pressable, Spinner, Text, VStack } from '@gluestack-ui/themed';
import { useContext, useState } from 'react';
import { ProgressBar } from '../ProgressBar';
import { TranslationContext } from '../../../contexts/translations/Translation';
import { PrimaryButton } from '../../buttons/PrimaryButton';
import { COLORS } from '../../../theme/colors';
import { StrainDTO } from '../../../generated/client-api-client';
import { BiochemicalTestMap } from '../../../utils/MapUtils';
import { pascalToSnake } from '../../../utils/StringUtils';

interface IStrainPredictionProps {
  strainResults: Record<string, number> | null | undefined;
  topStrainName: string;
  strain: StrainDTO | null;
  onProceedToVerification: () => void;
  onBack: () => void;
  testID?: string;
}

const DEFAULT_TOP_N = 3;

export const StrainPrediction = ({
  strainResults,
  topStrainName,
  strain,
  onProceedToVerification,
  onBack,
  testID,
}: IStrainPredictionProps) => {
  const { i18n } = useContext(TranslationContext);
  const [showAll, setShowAll] = useState(false);

  if (!strainResults) {
    return (
      <VStack gap={16} alignItems="center" paddingVertical={40}>
        <Spinner size="large" />
        <Text color={COLORS.NEUTRAL[90]}>{i18n?.t('page.analysis-details.info-card.no-results')}</Text>
      </VStack>
    );
  }

  const sorted = Object.entries(strainResults).sort(([, a], [, b]) => b - a);
  const displayed = showAll ? sorted : sorted.slice(0, DEFAULT_TOP_N);

  const neededTests = BiochemicalTestMap.filter(
    (key) => (strain as any)?.biochemicalTestNeeded?.[key],
  );

  const topConfidence = ((sorted[0]?.[1] ?? 0) * 100).toFixed(1);

  return (
    <VStack gap={14}>
      <VStack gap={2}>
        <Text fontWeight="bold" fontSize={16}>
          {i18n?.t('page.analysis-details.strain.title')}
        </Text>
        <Text fontSize={13} color={COLORS.NEUTRAL[90]}>
          {i18n?.t('page.analysis-details.strain.subtitle')}{' '}
          <Text fontSize={13} fontWeight="bold" color={COLORS.FOUNDATION_TERCIARY_100}>
            {i18n?.t('page.analysis-details.strain.subtitle-bold')}
          </Text>
        </Text>
      </VStack>

      <Box
        style={{
          backgroundColor: '#FFFBEA',
          borderRadius: 8,
          padding: 10,
          flexDirection: 'row',
          alignItems: 'center',
          gap: 8,
          borderWidth: 1,
          borderColor: '#FFE082',
        }}
      >
        <Icon as={AlertTriangleIcon} color={COLORS.STATUS.WARNING} size="sm" />
        <Text fontSize={12} color={COLORS.FOUNDATION_TERCIARY_100} flex={1}>
          {i18n?.t('page.analysis-details.strain.warning')}
        </Text>
      </Box>

      <VStack gap={10}>
        {displayed.map(([name, score]) => {
            return (
            <Box key={name} testID={`${testID ?? ''}strain_${name}`}>
              <HStack alignItems="center" gap={8}>
                <VStack flex={1}>
                  <Text fontSize={13} fontWeight={name === topStrainName ? 'bold' : 'normal'}>
                    {name}
                  </Text>
                </VStack>
                <Box flex={2}>
                  <ProgressBar progress={score} />
                </Box>
                <Text fontSize={13} fontWeight={name === topStrainName ? 'bold' : 'normal'} style={{ minWidth: 38, textAlign: 'right' }}>
                  {parseFloat((score * 100).toFixed(1))}%
                </Text>
              </HStack>
            </Box>
          );
        })}
      </VStack>

      {sorted.length > DEFAULT_TOP_N && (
        <Pressable onPress={() => setShowAll(!showAll)}>
          <Text fontSize={13} color={COLORS.ACCENT[100]}>
            {showAll
              ? i18n?.t('page.analysis-details.strain.hide')
              : i18n?.t('page.analysis-details.strain.show-all')}
          </Text>
        </Pressable>
      )}

      {neededTests.length > 0 && (
        <Box
          style={{
            backgroundColor: '#F0FBF5',
            borderRadius: 10,
            padding: 14,
            borderWidth: 1,
            borderColor: '#A8DFC0',
          }}
        >
          <Text fontWeight="bold" fontSize={14} marginBottom={2}>
            {i18n?.t('page.analysis-details.strain.recommended-tests-title', { strain: topStrainName })}
          </Text>
          <Text fontSize={12} color={COLORS.NEUTRAL[90]} marginBottom={10}>
            {i18n?.t('page.analysis-details.strain.recommended-tests-subtitle', {
              strain: topStrainName,
              confidence: topConfidence,
            })}
          </Text>
          <HStack paddingBottom={6} style={{ borderBottomWidth: 1, borderBottomColor: '#A8DFC0' }}>
            <Text fontWeight="bold" fontSize={12} flex={3}>{i18n?.t('page.analysis-details.strain.tests-table.test')}</Text>
            <Text fontWeight="bold" fontSize={12} flex={1} style={{ textAlign: 'center' }}>{i18n?.t('page.analysis-details.strain.tests-table.expected')}</Text>
          </HStack>
          {neededTests.map((key) => {
            const expected = (strain as any)?.[key];
            return (
              <HStack key={key} paddingVertical={6} style={{ borderBottomWidth: 1, borderBottomColor: '#D4EDDA' }} alignItems="center">
                <Text fontSize={12} flex={3} color={COLORS.FOUNDATION_TERCIARY_100}>
                  {i18n?.t(`properties.${pascalToSnake(key)}`)}
                </Text>
                <Box flex={1} alignItems="center">
                  <Box
                    style={{
                      width: 24,
                      height: 24,
                      borderRadius: 12,
                      backgroundColor: COLORS.STATUS.SUCCESS,
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Text fontSize={12} color={COLORS.NEUTRAL[0]} fontWeight="bold">
                      {expected
                        ? i18n?.t(`page.pathogen-details.plusMinusNA.${expected.toString()}`, { defaultValue: expected.toString() })
                        : '—'}
                    </Text>
                  </Box>
                </Box>
              </HStack>
            );
          })}
        </Box>
      )}

      <PrimaryButton
        onPress={async () => onProceedToVerification()}
        content={i18n?.t('page.analysis-details.strain.proceed-button')}
        width="100%"
        minWidth={0}
      />
      <Pressable onPress={() => onBack()} alignSelf="center">
        <Text fontSize={13} color={COLORS.ACCENT[100]}>
          {i18n?.t('page.analysis-details.strain.back-to-results')}
        </Text>
      </Pressable>
    </VStack>
  );
};
